import { DefaultRootState } from '@databases/redux';
import {
  DatabaseAttachment,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
} from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import svgToMiniDataURI from 'mini-svg-data-uri';

import { RECURSION_LIMIT } from './constants';
import { getEntryLinkTargetEntries } from './database/field-types/entryLink';
import {
  filterValidPageLinkValue,
  isExistingPageLinkValue,
} from './database/field-types/pageLink';
import {
  getConvertedDatabaseValue,
  getCoverField,
  getDatabaseEntryOptional,
  isBrokenDatabase,
} from './database/utils';
import { loadContentRef, loadCoverPicture } from './redux/content/actions';
import { selectContentRefOptional, selectCoverPicture } from './redux/content/selectors';
import { selectDatabaseByContentIdOptional } from './redux/databases/selectors';
import { ActionDependencyHelpers, SimpleStore } from './redux/redux';

const FALLBACK_COVER_IMAGES = [
  '<path d="M386 0H0v258h386V0Z" fill="#33FFC5"/><path opacity=".4" d="M385.999 0H239.763c-65.305 5.47-116.63 61.023-116.63 128.768 0 70.795 56.047 128.284 125.527 129.213h137.339V0Z" fill="#fff"/>',
  '<g clip-path="url(#a)"><path d="M387 0H0v258h387V0Z" fill="#BB9AE8"/><path opacity=".15" d="M0 0v258h32.444c.496-7.559.715-15.093 1.934-22.459 3.11-19.279 15.261-38.384 36.243-47.62 10.1-4.444 19.911-5.231 29.464-4.638l8.817.884c6.005.787 11.913 1.851 17.718 2.58 9.398 1.219 19.395 1.812 29.67-1.193 11.133-3.258 21.93-10.965 29.154-20.44 13.597-17.802 10.21-38.391 12.294-56.696 0-.303-.039-.613 0-.922a95.74 95.74 0 0 1 2.064-13.694c4.515-19.15 19.879-37.32 40.635-44.014 20.208-6.515 38.384.142 56.76 1.625 9.546.774 19.788.277 29.818-4.244 10.655-4.799 20.569-13.468 26.277-23.394 4.515-7.818 6.534-15.796 7.618-23.807L0 0Z" fill="#fff"/><path d="M387 0H0v258h387V0Z" fill="#5D07A0"/><path opacity=".1" d="M387 207.291 247.063 66.088 60.012 258H387v-50.709ZM0 130.417a26.91 26.91 0 0 0 26.264-26.85A26.908 26.908 0 0 0 0 76.62v53.797Z" fill="#fff"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h387v258H0z"/></clipPath></defs>',
  '<g clip-path="url(#a)"><path d="M387 0H0v256h387V0Z" fill="#92D0FF"/><path opacity=".3" d="M387.002 0H80.531a108.559 108.559 0 0 0 15.775 81.78c15.926 24.099 40.801 40.907 69.157 46.731 73.367 15.055 132.236 63.979 162.882 127.476h58.663L387.002 0Z" fill="#fff"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h387v256H0z"/></clipPath></defs>',
  '<g clip-path="url(#a)"><path d="M387 0H0v258h387V0Z" fill="#FFEB69"/><g opacity=".35" fill="#fff"><path opacity=".8" d="M243.075 232.335a30.453 30.453 0 0 0-8.911-21.531 30.431 30.431 0 0 0-21.527-8.919H0V258h228.975a30.406 30.406 0 0 0 14.1-25.665ZM212.637 97.698h-207c-1.891 0-3.779.18-5.637.535v59.792c1.859.35 3.746.527 5.637.528h207a30.44 30.44 0 0 0 30.001-30.44 30.442 30.442 0 0 0-30.001-30.441v.026Z"/></g></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h387v258H0z"/></clipPath></defs>',
  '<g clip-path="url(#a)"><path d="M386 0H0v258h386V0Z" fill="#FFBDCB"/><path opacity=".3" d="M386 126.602c-36.471 37.435-89.475 81.048-151.087 84.802-53.287 3.26-111.547-23.143-173.012-78.399A483.367 483.367 0 0 0 0 85.445V258h386V126.602Z" fill="#fff"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h386v258H0z"/></clipPath></defs>',
  '<g clip-path="url(#a)"><path d="M0 258h388V0H0v258Z" fill="#EF6F5D"/><path opacity=".2" d="M239.876 13.003a22.078 22.078 0 0 0 9.793 8.714c3.745 1.684 7.56 1.87 11.123 1.58 6.843-.548 13.622-3.031 21.155-.6a23.265 23.265 0 0 1 15.152 16.396 35.77 35.77 0 0 1 .768 5.096v.348c.774 6.818-.484 14.487 4.583 21.118a22.08 22.08 0 0 0 10.871 7.617c3.822 1.116 7.547.897 11.046.439 2.169-.278 4.37-.645 6.61-.968l3.286-.329a22.948 22.948 0 0 1 10.975 1.729 23.268 23.268 0 0 1 13.511 17.738c1.117 6.907-.064 14.189 3.919 21.11a22.114 22.114 0 0 0 9.819 8.721c3.738 1.683 7.553 1.864 11.116 1.58 1.466-.123 2.931-.329 4.403-.555V0H236.648c.272 4.36.775 8.746 3.228 13.003Z" fill="#fff"/><path opacity=".3" d="M0 206.477a26.956 26.956 0 0 0 18.25-8.296 26.91 26.91 0 0 0 7.463-18.594 26.91 26.91 0 0 0-7.463-18.593A26.957 26.957 0 0 0 0 152.697v53.78Z" fill="#fff"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h388v258H0z"/></clipPath></defs>',
  '<path d="M387 0H0v258h387V0Z" fill="#2B3556"/><path opacity=".1" d="M386.923 74.393a75.234 75.234 0 0 1-72.988-68.804A79.392 79.392 0 0 1 313.681 0h-70.349c0 3.908.176 7.621.508 11.47a145.563 145.563 0 0 0 143.083 133.134V74.393ZM0 94.911V258h40.002l63.138-62.57L0 94.911Z" fill="#fff"/>',
  '<path d="M386 0H0v258h386V0Z" fill="#0D0DFC"/><path opacity=".15" d="M0 258h136.963c6.725-11.269 20.407-15.707 20.407-33.632 0-23.784-24.09-23.784-24.09-47.587 0-23.803 24.09-23.783 24.09-47.567s-24.09-23.803-24.09-47.587c0-23.783 24.09-23.803 24.09-47.586 0-18.14-13.994-22.363-20.641-34.041H0v258Z" fill="#fff"/>',
  '<path d="M388 0H0v259h388V0Z" fill="#FFC420"/><path opacity=".3" d="M0 36.565V259h222.134L0 36.565Zm387.923 69.483V0H275.328a117.88 117.88 0 0 0 36.466 74.133 117.593 117.593 0 0 0 76.129 31.915Z" fill="#fff"/>',
].map((img) =>
  svgToMiniDataURI(
    `<svg width="386" height="258" fill="none" xmlns="http://www.w3.org/2000/svg">${img}</svg>`
  )
);

function getFallbackImage(entryId: string): string {
  let hash = 0;
  for (let i = 0; i < entryId.length; i++) {
    // See https://stackoverflow.com/a/7616484/242365
    hash = ((hash << 5) - hash + entryId.charCodeAt(i)) | 0;
  }
  return FALLBACK_COVER_IMAGES[Math.abs(hash) % FALLBACK_COVER_IMAGES.length];
}

export interface GetEntryCoverHelpers
  extends ActionDependencyHelpers<SimpleStore<DefaultRootState>> {
  selectDatabase: typeof selectDatabaseByContentIdOptional;
}

export function getEntryCover(
  {
    contentId,
    entryId,
    viewSettings,
  }: {
    contentId: string;
    entryId: string;
    viewSettings?: DatabaseViewSettings;
  },
  helpers: GetEntryCoverHelpers
): {
  coverImage:
    | Blob
    | DatabaseAttachment
    | { src: string }
    | { fileId: string }
    | undefined;
  fallback: { src: string };
} {
  const { selector, selectDatabase } = helpers;

  const [coverField, coverValue] = selector((state) => {
    const rawDatabase = selectDatabase(state, { contentId });
    const database =
      rawDatabase && !isBrokenDatabase(rawDatabase) ? rawDatabase : undefined;
    const entry = database && getDatabaseEntryOptional(database, entryId);
    const coverField =
      database && getCoverField(database, viewSettings?.layout.coverFieldId);
    const coverValue =
      database &&
      entry &&
      coverField &&
      getConvertedDatabaseValue(database, coverField, entry, state);
    return [coverField, coverValue] as const;
  });

  const fallback = { src: getFallbackImage(entryId) };

  if (!coverField || !coverValue) {
    return { coverImage: undefined, fallback };
  } else {
    return {
      coverImage: getEntryCoverInternal({ coverField, coverValue, entryId }, helpers),
      fallback,
    };
  }
}

function getEntryCoverInternal(
  params: {
    coverField: DatabaseField;
    coverValue?: DatabaseValue;
    entryId: string;
    recursionTracker?: { recursionCount: number };
  },
  helpers: GetEntryCoverHelpers
): Blob | DatabaseAttachment | { src: string } | { fileId: string } | undefined {
  const {
    coverField,
    coverValue,
    entryId,
    recursionTracker = { recursionCount: 0 },
  } = params;
  const { dispatch, selector, selectDatabase } = helpers;

  if (coverValue && recursionTracker.recursionCount < RECURSION_LIMIT) {
    if (coverValue.type === DatabaseFieldType.ENTRY_LINK) {
      const resolvedCoverValue = selector((state) => {
        const targetEntry = getEntryLinkTargetEntries(
          { field: coverField, value: coverValue.value },
          state,
          { selectDatabase }
        )[0];
        const targetCoverField = targetEntry
          ? getCoverField(targetEntry.database)
          : undefined;
        return targetEntry && targetCoverField
          ? getConvertedDatabaseValue(
              targetEntry.database,
              targetCoverField,
              targetEntry.entry,
              state,
              recursionTracker
            )
          : undefined;
      });

      return getEntryCoverInternal(
        {
          coverField,
          coverValue: resolvedCoverValue,
          entryId,
          recursionTracker: { recursionCount: recursionTracker.recursionCount + 1 },
        },
        helpers
      );
    } else if (coverValue.type === DatabaseFieldType.PAGE_LINK) {
      const contentRef = filterValidPageLinkValue(coverValue.value).filter((v) =>
        isExistingPageLinkValue(v)
      )[0];

      if (contentRef) {
        dispatch(loadContentRef(contentRef));

        const resolvedContentRef = selector((state) =>
          selectContentRefOptional(state, { contentRef })
        );
        if (resolvedContentRef) {
          dispatch(loadCoverPicture(resolvedContentRef.contentId));

          const coverPicture = selector((state) =>
            selectCoverPicture(state, { contentId: resolvedContentRef.contentId })
          );
          if (coverPicture) {
            return { src: coverPicture };
          }
        }
      }
    } else if (coverValue.type === DatabaseFieldType.ATTACHMENT) {
      return coverValue.value[0];
    }
  }
}
